import { useState, useMemo } from 'react';
import useDealStore from '../store/dealStore';
import useAuthStore from '../store/authStore';
import deals from '../data/deals';
import '../styles/deal-entry.css';

// Preview up to 3 of the top-scored static deals as "live deal" teasers
const PREVIEW_COUNT = 3;

function DealEntryPage() {
  const { forceNavigateToModule, navigateToModule } = useDealStore();
  const { user } = useAuthStore();
  const [hoveredDeal, setHoveredDeal] = useState(null);

  // Pick the top 3 by score for the preview cards
  const previewDeals = useMemo(
    () => [...deals].sort((a, b) => b.score - a.score).slice(0, PREVIEW_COUNT),
    []
  );

  const handleUploadPath = () => {
    forceNavigateToModule(3);
  };

  const handleSeeMoreDeals = () => {
    forceNavigateToModule(1);
  };

  const handlePreviewDealSelect = (deal) => {
    // Navigate to Module 1 so user can officially select and confirm the deal
    forceNavigateToModule(1);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#1d4ed8';
    if (score >= 60) return '#1d4ed8';
    return '#0f2d5e';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Strong Buy';
    if (score >= 60) return 'Review';
    return 'Pass';
  };

  return (
    <div className="deal-entry-page">

      {/* ── Page Header ── */}
      <div className="de-header">
        <div className="de-header-left">
          <div className="de-badge">Deal Entry</div>
          <h1 className="de-title">Start Your Acquisition</h1>
          <p className="de-subtitle">
            Choose how you want to begin — upload your own documents or browse live deals on market.
          </p>
        </div>
        {user && (
          <div className="de-user-context">
            <span className="de-user-name">{user.name}</span>
          </div>
        )}
      </div>

      {/* ── OPTION A — Manual Upload (prominent, top) ── */}
      <div className="de-section de-section-upload">
        <div className="de-section-label-row">
          <span className="de-section-tag de-tag-primary">Option A</span>
          <h2 className="de-section-heading">Upload Your Deal Documents</h2>
        </div>
        <p className="de-section-desc">
          Have your own Offering Memorandum, rent roll, or T12? Upload directly and let AcquireOS extract
          the financials for you — no LoopNet connection required.
        </p>

        <button className="de-upload-cta" onClick={handleUploadPath}>
          <div className="de-upload-cta-icon">Docs</div>
          <div className="de-upload-cta-body">
            <span className="de-upload-cta-title">Upload Deal Documents</span>
            <span className="de-upload-cta-sub">OM · Rent Roll · T12 / P&amp;L · Photos</span>
          </div>
          <div className="de-upload-cta-arrow">→</div>
        </button>

        <div className="de-upload-features">
          <div className="de-upload-feature">
            <span>AI extracts NOI, cap rate &amp; financials</span>
          </div>
          <div className="de-upload-feature">
            <span>Routes directly to underwriting</span>
          </div>
          <div className="de-upload-feature">
            <span>No LoopNet account needed</span>
          </div>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="de-divider">
        <span>or</span>
      </div>

      {/* ── OPTION B — Browse Live Deals ── */}
      <div className="de-section de-section-live">
        <div className="de-section-label-row">
          <span className="de-section-tag de-tag-secondary">Option B</span>
          <h2 className="de-section-heading">Browse Live Deals Currently on Market</h2>
        </div>
        <p className="de-section-desc">
          Explore curated multifamily deals with AI-scored analysis. Select a deal to begin underwriting.
        </p>

        <div className="de-preview-grid">
          {previewDeals.map((deal) => (
            <div
              key={deal.id}
              className={`de-preview-card ${hoveredDeal === deal.id ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredDeal(deal.id)}
              onMouseLeave={() => setHoveredDeal(null)}
              onClick={() => handlePreviewDealSelect(deal)}
            >
              <div className="de-preview-score-badge" style={{ background: getScoreColor(deal.score) + '20', color: getScoreColor(deal.score), borderColor: getScoreColor(deal.score) + '40' }}>
                {deal.score}/100 &nbsp;·&nbsp; {getScoreLabel(deal.score)}
              </div>
              <div className="de-preview-name">{deal.name}</div>
              <div className="de-preview-location">📍 {deal.city}, {deal.state}</div>
              <div className="de-preview-metrics">
                <div className="de-preview-metric">
                  <span>Price</span>
                  <strong>${deal.price?.toLocaleString()}</strong>
                </div>
                <div className="de-preview-metric">
                  <span>Units</span>
                  <strong>{deal.units}</strong>
                </div>
                {deal.capRate && (
                  <div className="de-preview-metric">
                    <span>Cap Rate</span>
                    <strong>{deal.capRate}%</strong>
                  </div>
                )}
              </div>
              <div className="de-preview-select-hint">Click to select →</div>
            </div>
          ))}
        </div>

        <button className="de-see-more-btn" onClick={handleSeeMoreDeals}>
          <span>See More Deals</span>
          <span className="de-see-more-count">({deals.length} available)</span>
          <span className="de-see-more-arrow">→</span>
        </button>
      </div>

      {/* ── Footer info ── */}
      <div className="de-footer-note">
        <span>💡</span>
        <span>
          Both paths lead to the Underwriting Engine. Market Intelligence is available anytime from the header.
        </span>
      </div>

    </div>
  );
}

export default DealEntryPage;
